import { Link } from 'react-router-dom';
import { Button, Card, EmptyState, LoadingList, SectionHeader, cx } from '@timely/ui';
import {
    CHEQUE_STATES, WORKLIST_GROUPS, dayIsClear, holds, worklistQuery,
    type ChequeState, type WorklistGroupDefinition,
} from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { FormNotice } from './AuthLayout';
import { useFormatters } from '../book/useFormatters';
import { useToday, type MemberWorkload } from '../book/useToday';
import { useOnboarding } from '../onboarding/useOnboarding';
import { WorkspaceHome } from './WorkspaceHome';

/**
 * The screen somebody opens in the morning.
 *
 * It answers one question — **what needs doing now** — and every figure on it
 * is a door. A card saying "12 overdue" opens a list of exactly those twelve:
 * the count comes from `worklist_summary()` and the link from
 * `worklistQuery()`, and both are built from the one filter that defines the
 * group. There is no second definition of "overdue" for them to drift apart
 * from, and `supabase/tests/12` asserts each count against `book_totals()`
 * under the same filter.
 *
 * It is deliberately not a dashboard. No decorative tiles, no charts, no
 * figure that cannot be opened. A collector should be able to read it in five
 * seconds, press the first thing, and start working.
 *
 * What a person sees is decided by what they may see: the day is computed by a
 * `security invoker` function, so a collector's counts are of their own
 * accounts without this file filtering anything. The team strip appears only
 * with `customers.view.all`, because a team figure is meaningless — and
 * misleading — when it is a total of one person's share.
 */
export function Today() {
    const context = useActiveWorkspace();
    const { workspace } = context;
    const { money, count } = useFormatters();

    const canSeeTeam = holds(context, 'customers.view.all') && holds(context, 'team.view');
    const canSeeCheques = holds(context, 'cheques.view');

    const { totals, team, cheques, chequesUnavailable, loading, error, reload } =
        useToday(context, { withTeam: canSeeTeam, withCheques: canSeeCheques });
    const onboarding = useOnboarding(workspace.id);

    const book = (query: string) => `/w/${workspace.slug}/book${query ? `?${query}` : ''}`;

    if (loading || onboarding.loading) {
        return (
            <Page>
                <SectionHeader title="Today" subtitle="What needs doing now." />
                <Card className="mt-5 overflow-hidden"><LoadingList label="Loading your day" rows={4} /></Card>
            </Page>
        );
    }

    if (error) {
        return (
            <Page>
                <SectionHeader title="Today" subtitle="What needs doing now." />
                <div className="mt-4"><FormNotice tone="error">{error}</FormNotice></div>
                <div className="mt-4">
                    <Button variant="secondary" onClick={() => { void reload(); }}>Try again</Button>
                </div>
            </Page>
        );
    }

    // A company with no accounts at all is not having a quiet day. Which
    // screen that means depends on whether this company has ever finished
    // onboarding: never — the checklist is the right screen, and showing
    // six zeroes instead would be six ways of saying nothing. Once — most
    // often because it just ran a Fresh Start (0027) — it is a mature
    // company between imports, not a new one, and the checklist must not
    // reappear; `workspace_onboarding.completed_at` is exactly the durable
    // fact that tells the two apart, and Fresh Start is deliberately
    // catalogued to leave it alone.
    if (!totals || totals.total.count === 0) {
        if (!onboarding.completedAt) return <WorkspaceHome />;
        return (
            <Page>
                <SectionHeader title="Today" subtitle="What needs doing now." />
                <Card className="mt-5">
                    <EmptyState
                        title="No accounts right now"
                        hint="Nothing is assigned to the book at the moment. A new import will bring accounts back."
                        action={<Link to={`/w/${workspace.slug}/data`} className="text-accent hover:underline text-[13.5px] font-semibold">Go to the data source</Link>}
                    />
                </Card>
            </Page>
        );
    }

    const clear = dayIsClear(totals);

    return (
        <Page>
            <SectionHeader
                title="Today"
                subtitle="What needs doing now — every figure opens the accounts behind it."
                actions={
                    <div className="text-right">
                        <div className="text-[17px] font-bold text-label num tabular-nums">
                            {money.exact(totals.total.amount)}
                        </div>
                        <div className="text-[12px] text-label-3">
                            Owed across {count(totals.total.count)}{' '}
                            {totals.total.count === 1 ? 'account' : 'accounts'}
                        </div>
                    </div>
                }
            />

            {clear && (
                <Card className="mt-5">
                    <EmptyState
                        title="Nothing is waiting"
                        hint="No follow-up is overdue, none is due today, nothing is flagged, and every account that owes has a date. Work ahead if you like, or leave it."
                        action={
                            totals.groups.future.count > 0 ? (
                                <Link
                                    to={book(worklistQuery('future'))}
                                    className="text-accent font-semibold hover:underline"
                                >
                                    See the {count(totals.groups.future.count)} scheduled ahead
                                </Link>
                            ) : undefined
                        }
                    />
                </Card>
            )}

            {/* The work itself. Ordered as somebody works it: what was missed,
                then what was planned for today, then what has been flagged. */}
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {WORKLIST_GROUPS.map(group => (
                    <WorkCard
                        key={group.group}
                        group={group}
                        figure={totals.groups[group.group]}
                        to={book(worklistQuery(group.group))}
                    />
                ))}
            </div>

            {/* What customers said they would pay. A promise is not money, and
                the wording never calls it money that has arrived. */}
            {totals.promisedOpen.count > 0 && (
                <>
                    <div className="mt-9"><SectionHeader title="Expected collections" /></div>
                    <Card className="mt-5 px-5 py-4">
                        <div className="flex items-start gap-6 flex-wrap">
                            <Promised label="Promised for today" figure={totals.promisedToday} />
                            <Promised label="Promised this week" figure={totals.promisedWeek} />
                            <Promised label="All open promises" figure={totals.promisedOpen} />
                        </div>
                        <p className="text-[12px] text-label-3 mt-3 leading-snug max-w-prose">
                            What customers have committed to, not what has arrived. A promise is
                            recorded when somebody says a date and an amount on a call.
                        </p>
                    </Card>
                </>
            )}

            {canSeeCheques && cheques && (
                <ChequeAttention
                    slug={workspace.slug}
                    cheques={cheques}
                    unavailable={chequesUnavailable}
                />
            )}

            {canSeeTeam && team.length > 1 && (
                <TeamWork team={team} slug={workspace.slug} />
            )}
        </Page>
    );
}

/* -------------------------------------------------------------------------- */

const TONE_TEXT = {
    dang: 'text-dang', warn: 'text-warn', brand: 'text-accent',
    pos: 'text-pos', neutral: 'text-label',
} as const;

/**
 * One group of the day.
 *
 * The whole card is the link, because the count is the thing somebody wants to
 * press and a small "view" link beside a big number is a smaller target for no
 * reason. A group with nothing in it stays on screen and says so — a card that
 * vanished when it emptied would make the screen move under somebody's hand
 * every morning, and "no follow-up is overdue" is worth reading.
 */
function WorkCard({
    group, figure, to,
}: {
    group: WorklistGroupDefinition;
    figure: { count: number; amount: number };
    to: string;
}) {
    const { money, count } = useFormatters();
    const empty = figure.count === 0;

    const body = (
        <>
            <div className="flex items-baseline justify-between gap-3">
                <span className="text-[13px] font-semibold text-label">{group.label}</span>
                <span className={cx(
                    'text-[22px] font-bold num tabular-nums leading-none',
                    empty ? 'text-label-3' : TONE_TEXT[group.tone],
                )}>
                    {count(figure.count)}
                </span>
            </div>
            <div className="text-[12px] text-label-3 mt-1.5 leading-snug">
                {empty ? group.empty : group.hint}
            </div>
            {!empty && (
                <div className="text-[12.5px] text-label-2 num mt-2">
                    {money.compact(figure.amount)} owed
                </div>
            )}
        </>
    );

    if (empty) {
        return <Card className="px-4 py-3.5 min-h-[104px]">{body}</Card>;
    }

    return (
        <Link
            to={to}
            // Named for a screen reader, because the visible text is a label, a
            // number and a sentence in three elements, and "Overdue 12 the date
            // you set has gone by 340,000 owed" is not a link name.
            aria-label={`${group.label}: ${count(figure.count)} ${figure.count === 1 ? 'account' : 'accounts'}`}
            className="block rounded-[14px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
            <Card className="px-4 py-3.5 min-h-[104px] hover:border-separator-strong transition-colors">
                {body}
            </Card>
        </Link>
    );
}

function Promised({ label, figure }: { label: string; figure: { count: number; amount: number } }) {
    const { money, count } = useFormatters();
    return (
        <div>
            <div className="text-[17px] font-bold text-label num tabular-nums">
                {money.exact(figure.amount)}
            </div>
            <div className="text-[12px] text-label-3">
                {label} · {count(figure.count)} {figure.count === 1 ? 'account' : 'accounts'}
            </div>
        </div>
    );
}

/**
 * The cheques that need something done to them.
 *
 * Four of the six states, because "upcoming" and "cleared" are not work. Each
 * figure opens the register already filtered to it, and both numbers come from
 * `cheque_totals()` — the same call the register's tiles make — so Today and
 * the register cannot disagree about how many cheques bounced.
 */
function ChequeAttention({
    slug, cheques, unavailable,
}: {
    slug: string;
    cheques: { byState: Record<ChequeState, { count: number; amount: number }> };
    unavailable: boolean;
}) {
    const { money, count } = useFormatters();
    const needs: ChequeState[] = ['overdue', 'due', 'bounced', 'hold'];
    const anything = needs.some(state => cheques.byState[state].count > 0);

    if (unavailable) {
        return (
            <>
                <div className="mt-9"><SectionHeader title="Cheques" /></div>
                <Card className="mt-5 px-5 py-4">
                    <p className="text-[13px] text-label-3">
                        We could not read your cheque figures just now.
                    </p>
                </Card>
            </>
        );
    }

    if (!anything) return null;

    return (
        <>
            <div className="mt-9">
                <SectionHeader
                    title="Cheques needing attention"
                    actions={
                        <Link to={`/w/${slug}/cheques`} className="text-[13px] text-accent font-semibold hover:underline">
                            Open the register
                        </Link>
                    }
                />
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {needs.filter(state => cheques.byState[state].count > 0).map(state => (
                    <Link
                        key={state}
                        to={`/w/${slug}/cheques?state=${state}`}
                        aria-label={`${CHEQUE_STATES[state].label}: ${count(cheques.byState[state].count)} cheques`}
                        className="block rounded-[14px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    >
                        <Card className="px-4 py-3.5 hover:border-separator-strong transition-colors">
                            <div className="flex items-baseline justify-between gap-3">
                                <span className="text-[13px] font-semibold text-label">
                                    {CHEQUE_STATES[state].label}
                                </span>
                                <span className={cx(
                                    'text-[20px] font-bold num tabular-nums leading-none',
                                    TONE_TEXT[CHEQUE_STATES[state].tone],
                                )}>
                                    {count(cheques.byState[state].count)}
                                </span>
                            </div>
                            <div className="text-[12.5px] text-label-2 num mt-1.5">
                                {money.compact(cheques.byState[state].amount)}
                            </div>
                        </Card>
                    </Link>
                ))}
            </div>
        </>
    );
}

/**
 * Who on the team has work waiting.
 *
 * Operational, not analytical: the question it answers is "is anybody
 * drowning", and it sorts by the number of overdue follow-ups because that is
 * the one that means somebody needs help today. Anything deeper belongs in
 * Reports.
 */
function TeamWork({ team, slug }: { team: MemberWorkload[]; slug: string }) {
    const { money, count } = useFormatters();

    return (
        <>
            <div className="mt-9"><SectionHeader title="Who has work waiting" /></div>
            <Card className="mt-5 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-[13px]">
                        <thead>
                            <tr className="text-left text-label-3 text-[12px]">
                                <th scope="col" className="font-semibold px-5 py-2.5">Person</th>
                                <th scope="col" className="font-semibold px-3 py-2.5 text-right">Overdue</th>
                                <th scope="col" className="font-semibold px-3 py-2.5 text-right">Due today</th>
                                <th scope="col" className="font-semibold px-3 py-2.5 text-right">Urgent</th>
                                <th scope="col" className="font-semibold px-3 py-2.5 text-right">Accounts</th>
                                <th scope="col" className="font-semibold px-5 py-2.5 text-right">Owed</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-separator">
                            {team.map(member => (
                                <tr key={member.memberId}>
                                    <td className="px-5 py-2.5">
                                        <Link
                                            to={`/w/${slug}/book?owner=${member.memberId}`}
                                            className="text-label font-semibold hover:text-accent hover:underline"
                                        >
                                            {member.displayName}
                                        </Link>
                                    </td>
                                    <td className={cx('px-3 py-2.5 text-right num tabular-nums',
                                        member.overdueCount > 0 ? 'text-dang font-semibold' : 'text-label-3')}>
                                        {count(member.overdueCount)}
                                    </td>
                                    <td className="px-3 py-2.5 text-right num tabular-nums text-label-2">
                                        {count(member.todayCount)}
                                    </td>
                                    <td className="px-3 py-2.5 text-right num tabular-nums text-label-2">
                                        {count(member.urgentCount)}
                                    </td>
                                    <td className="px-3 py-2.5 text-right num tabular-nums text-label-2">
                                        {count(member.openCount)}
                                    </td>
                                    <td className="px-5 py-2.5 text-right num tabular-nums text-label">
                                        {money.compact(member.openAmount)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        </>
    );
}
